import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  calculateSeriesOrderSummary,
  seriesCheckout,
  validateSeriesPromoCode,
} from './series-cart-thunk';
import { SeriesCartState } from './series-cart-types';

export const initialState: SeriesCartState = {
  loadingOrderSummary: false,
  seriesPaymentError: '',
  selectedTicketOrderSummary: {},
  seriesOrderExternalId: '',
  seriesCartTotal: 0,
  error: '',
  paymentStatus: '',
  promoCode: {
    code: '',
    status: 'unset',
    error: null,
  },
};

const seriesCartSlice = createSlice({
  name: 'seriesCart',
  initialState,
  reducers: {
    setLoadingSummary(state) {
      state.loadingOrderSummary = true;
    },
    unsetLoadingSummary(state) {
      state.loadingOrderSummary = false;
    },
    clearSeriesCart(state) {
      state.selectedTicketOrderSummary = {};
      state.seriesOrderExternalId = '';
      state.seriesCartTotal = 0;
    },
    setSeriesCartTotal(state, action: PayloadAction<number>) {
      state.seriesCartTotal = action.payload;
    },
    setPromoCode(state, action: PayloadAction<string>) {
      state.promoCode.code = action.payload;

      if (action.payload.length === 0) {
        state.promoCode.status = 'unset';
        state.promoCode.error = null;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(calculateSeriesOrderSummary.pending, state => {
        state.loadingOrderSummary = true;
      })
      .addCase(calculateSeriesOrderSummary.fulfilled, (state, action) => {
        try {
          const { orders, external_id, total_price_cents, selectedTicketIds } =
            action.payload;

          if (!state.seriesOrderExternalId) {
            state.seriesOrderExternalId = external_id;
          }

          state.selectedTicketOrderSummary = {};
          state.error = '';
          if (orders.length > 0) {
            Object.entries(selectedTicketIds).forEach(
              ([eventExternalId, _]) => {
                const order = orders.find(
                  order => order.event_external_id === eventExternalId,
                );
                if (order) {
                  state.selectedTicketOrderSummary[eventExternalId] = {
                    id: order.id,
                    slug: order.slug,
                    total_price_cents: order.total_price_cents,
                    line_items: order.line_items,
                    first_name: order.first_name,
                    last_name: order.last_name,
                    email: order.email,
                    event_external_id: order.event_external_id,
                  };
                }
              },
            );
          }
          state.seriesCartTotal = total_price_cents;
          state.loadingOrderSummary = false;
        } catch (error) {
          state.loadingOrderSummary = false;
          state.error = 'Failed to calculate series order summary';
        }
      })
      .addCase(calculateSeriesOrderSummary.rejected, (state, action) => {
        if (!action?.payload?.eventId) {
          state.error =
            action?.payload?.errors?.[0] ||
            'Failed to calculate series order summary';
        }
        state.loadingOrderSummary = false;
      })
      .addCase(seriesCheckout.pending, state => {
        state.seriesPaymentError = '';
        state.paymentStatus = 'pending';
      })
      .addCase(seriesCheckout.fulfilled, state => {
        state.seriesPaymentError = '';
        state.paymentStatus = 'success';
      })
      .addCase(seriesCheckout.rejected, (state, action) => {
        state.seriesPaymentError =
          action?.payload?.error || 'Failed to confirm series payment';

        state.paymentStatus = 'failure';
      })
      .addCase(validateSeriesPromoCode.pending, state => {
        state.promoCode.status = 'pending';
        state.promoCode.error = null;
      })
      .addCase(
        validateSeriesPromoCode.fulfilled,
        (
          state,
          action: PayloadAction<{ promoCode: string; valid: boolean }>,
        ) => {
          if (action.payload.valid) {
            state.promoCode = {
              code: action.payload.promoCode,
              status: 'valid',
              error: null,
            };
          } else {
            state.promoCode = {
              code: action.payload.promoCode,
              status: 'invalid',
              error: 'series.promo-code-invalid',
            };
          }
        },
      )
      .addCase(validateSeriesPromoCode.rejected, (state, action) => {
        state.promoCode = {
          code: action?.payload?.promoCode || '',
          status: 'error',
          error:
            typeof action?.payload?.error === 'string'
              ? action.payload.error
              : 'series.promo-code-error',
        };
      });
  },
});

export const {
  setLoadingSummary,
  clearSeriesCart,
  setSeriesCartTotal,
  unsetLoadingSummary,
  setPromoCode,
} = seriesCartSlice.actions;

export const seriesCartReducer = seriesCartSlice.reducer;
